import { useTranslation } from 'react-i18next';
import { extractYouTubeId, extractVimeoId } from '../utils/videoPortfolio';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";

// Same per-item rendering as the worker portfolio (WorkerDashboard / WorkerProfile): YouTube
// and Vimeo embed as iframes, uploaded files play in a native <video>. Additions: uploads use
// thumbnail_url as the poster (preload="metadata" so a grid of videos doesn't pull every file),
// and a 'light' variant for the white public union page.
const VARIANTS = {
  dark: {
    card:  { background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)' },
    title: WHITE,
    desc:  'rgba(255,255,255,0.55)',
    del:   'rgba(255,255,255,0.35)',
  },
  light: {
    card:  { background: WHITE, border: '1px solid rgba(13,27,42,0.08)', boxShadow: '0 2px 16px rgba(13,27,42,0.08)' },
    title: NAVY,
    desc:  '#666',
    del:   '#999',
  },
};

export function PortfolioMedia({ item }) {
  const ytId    = item.video_type === 'youtube' ? extractYouTubeId(item.video_url) : null;
  const vimeoId = item.video_type === 'vimeo'   ? extractVimeoId(item.video_url)   : null;
  const frame = { position: 'relative', paddingTop: '56.25%', background: '#000' };
  const fill  = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' };

  if (ytId) {
    return (
      <div style={frame}>
        <iframe src={`https://www.youtube.com/embed/${ytId}`} style={fill} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={item.title || 'Video'} />
      </div>
    );
  }
  if (vimeoId) {
    return (
      <div style={frame}>
        <iframe src={`https://player.vimeo.com/video/${vimeoId}`} style={fill} loading="lazy" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={item.title || 'Video'} />
      </div>
    );
  }
  if (item.video_type === 'upload' && item.video_url) {
    return (
      <div style={frame}>
        <video
          controls
          playsInline
          preload="metadata"
          poster={item.thumbnail_url || undefined}
          src={item.video_url}
          style={{ ...fill, objectFit: 'contain', background: '#000' }}
        />
      </div>
    );
  }
  return null;
}

export default function PortfolioGallery({ items = [], variant = 'dark', onDelete, deletingId }) {
  const { t } = useTranslation();
  const v = VARIANTS[variant] || VARIANTS.dark;
  if (!items.length) return null;

  return (
    <div
      className="portfolio-gallery-grid"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '16px' }}
    >
      {items.map(item => (
        <div key={item.id} data-portfolio-item={item.id} style={{ ...v.card, borderRadius: '12px', overflow: 'hidden', fontFamily: FONT_BODY, minWidth: 0 }}>
          <PortfolioMedia item={item} />
          <div style={{ padding: '12px 16px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {item.title && (
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: v.title, marginBottom: item.description ? '4px' : 0, overflowWrap: 'anywhere' }}>{item.title}</div>
              )}
              {item.description && (
                <div style={{ fontSize: '0.82rem', color: v.desc, lineHeight: 1.5, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{item.description}</div>
              )}
            </div>
            {onDelete && (
              <button
                onClick={() => onDelete(item)}
                disabled={deletingId === item.id}
                aria-label={t('portfolio.deleteAria', { title: item.title || '' })}
                style={{ background: 'none', border: 'none', color: v.del, fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 2px', flexShrink: 0, opacity: deletingId === item.id ? 0.4 : 1, minWidth: '28px', minHeight: '28px' }}
              >×</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
